import { produce } from "immer";
import StyledInput from "notes-and-forms-v2/components/styled-input/StyledInput";
import AnswerFieldContent from "../../answer-field-content/AnswerFieldContent";
import {
  ElementViewForClipboardProps,
  ElementViewProps,
} from "../AtomicElementView";
import styles from "./CheckboxesContent.module.scss";

interface CheckboxesData {
  selectedOptions: {
    id: string;
    title: string;
    answerFields?: { id: string; title: string; value: string }[];
  }[];
  otherOption?: {
    value: string;
  };
}

function parseRawData(data: any, element: CheckboxesElement): CheckboxesData {
  if (!data) {
    return {
      selectedOptions: [],
    };
  }

  if (typeof data === "object" && !Array.isArray(data)) {
    // We assume the data is in the current / latest format, but ensure it has the selectedOptions array
    return { ...data, selectedOptions: data.selectedOptions ?? [] };
  }

  // Otherwise, the data can be:
  // - an array of option title strings (saved by the initial implementation of RadiosContent)
  // - an array of option id strings (returned by GradientJ)
  // - a string containing a comma-separated list of option titles or ids (potentially returned by GradientJ)

  const values = Array.isArray(data) ? data : data.toString().split(",");

  const parsedData: CheckboxesData = {
    selectedOptions: [],
  };

  values.forEach((value) => {
    const option = element.options.find(
      (option) => option.title === value || option.id === value,
    );
    if (option) {
      parsedData.selectedOptions.push({
        id: option.id,
        title: option.title,
      });
    } else if (element.enableOtherOption && value && !parsedData.otherOption) {
      parsedData.otherOption = {
        value,
      };
    }
  });

  return parsedData;
}

function selectOption(data: CheckboxesData, option: TemplateElementOption) {
  return produce(data, (draft) => {
    draft.selectedOptions.push({
      id: option.id,
      title: option.title,
    });
  });
}

function unselectOption(data: CheckboxesData, option: TemplateElementOption) {
  return produce(data, (draft) => {
    draft.selectedOptions = draft.selectedOptions.filter(
      (item) => item.id !== option.id,
    );
  });
}

function toggleOption(data: CheckboxesData, option: TemplateElementOption) {
  return produce(data, (draft) => {
    if (draft.selectedOptions.some(({ id }) => id === option.id)) {
      draft.selectedOptions = draft.selectedOptions.filter(
        (item) => item.id !== option.id,
      );
      return;
    }

    draft.selectedOptions.push({
      id: option.id,
      title: option.title,
    });
  });
}

export function CheckboxesContentForClipboard({
  data: rawData,
  element,
}: ElementViewForClipboardProps<CheckboxesElement, CheckboxesData>) {
  const data = parseRawData(rawData, element);

  if (!data.selectedOptions.length && !data.otherOption?.value) {
    return <div>No answer</div>;
  }

  return (
    <ul>
      {data.selectedOptions.map((item) => (
        <li key={item.id}>
          {item.title}
          {item.answerFields?.map((answerField) => (
            <div>{answerField.value}</div>
          ))}
        </li>
      ))}
      {data.otherOption?.value && <li>{data.otherOption.value}</li>}
    </ul>
  );
}

export default function CheckboxesContent({
  data: rawData,
  element,
  onChange,
  isLocked,
  isDownloadMode,
  disabled,
  options,
}: ElementViewProps<CheckboxesElement, CheckboxesData>) {
  const data = parseRawData(rawData, element);

  return (
    <div className={styles.container}>
      <div>
        {element.options.map((option) => {
          const isSelected = data.selectedOptions.some(
            ({ id }) => id === option.id,
          );

          if (options?.hideUnselectedOptionsForCheckboxes && !isSelected) {
            return null;
          }

          return (
            <div className={styles["option-row"]} key={option.id}>
              <div className={styles.option}>
                <StyledInput
                  type="checkbox"
                  checked={isSelected}
                  onChange={(event) => {
                    if (event.target.checked) {
                      onChange(selectOption(data, option));
                      return;
                    }
                    onChange(unselectOption(data, option));
                  }}
                  disabled={disabled || isLocked}
                  disableStyling={isDownloadMode}
                />
                <label
                  onClick={() => {
                    if (disabled || isLocked) {
                      return;
                    }

                    onChange(toggleOption(data, option));
                  }}
                >
                  <div className={styles["option__title"]}>{option.title}</div>
                </label>
              </div>
              {isSelected &&
                option.answerFields?.map((answerField) => (
                  <AnswerFieldContent
                    key={answerField.id}
                    answerField={answerField}
                    data={data.selectedOptions
                      .find((el) => el.id === option.id)
                      ?.answerFields?.find((el) => el.id === answerField.id)}
                    onChange={(answerFieldData) =>
                      onChange(
                        produce(data, (draft) => {
                          const optionToUpdate = draft.selectedOptions.find(
                            (el) => el.id === option.id,
                          );

                          if (!optionToUpdate) {
                            return;
                          }

                          optionToUpdate.answerFields = [answerFieldData];
                        }),
                      )
                    }
                    disabled={disabled || isLocked}
                  />
                ))}
            </div>
          );
        })}
        {element.enableOtherOption &&
          (!options?.hideUnselectedOptionsForCheckboxes ||
            data.otherOption) && (
            <div className={styles["option-row"]}>
              <div className={styles.option}>
                <StyledInput
                  type="checkbox"
                  checked={!!data.otherOption}
                  onChange={(event) => {
                    if (event.target.checked) {
                      onChange(
                        produce(data, (draft) => {
                          draft.otherOption = {
                            value: "",
                          };
                        }),
                      );
                      return;
                    }
                    onChange(
                      produce(data, (draft) => {
                        delete draft.otherOption;
                      }),
                    );
                  }}
                  disabled={disabled || isLocked}
                  disableStyling={isDownloadMode}
                />
                <label>
                  <div className={styles["option__title"]}>
                    Other:
                    <input
                      className={styles["option__title-input"]}
                      placeholder="Please specify"
                      value={data.otherOption?.value ?? ""}
                      onChange={(e) => {
                        onChange(
                          produce(data, (draft) => {
                            draft.otherOption = {
                              value: e.target.value,
                            };
                          }),
                        );
                      }}
                    />
                  </div>
                </label>
              </div>
            </div>
          )}
      </div>
    </div>
  );
}
